import {
  Meal,
  Symptom,
  CorrelationMatch,
  LongitudinalIntoleranceItem,
  IntoleranceStatus,
  TransitPhase,
  ClinicalFoodCategory,
  TransitSpeed,
  TransitWindowConfig,
  FodmapSubtype,
  FodmapSubtypeCorrelation,
  InferredConstipationPeriod,
  MotilityKineticsSummary,
  UserFoodCorrection,
} from './types';
import { calculateLagHours } from './dateUtils';
import { canonicalizeIngredient, DESCRIPTOR_STOPWORDS, normalizeFoodDatabaseTitle } from './ingredientUtils';
import {
  isZeroFodmapFood,
  calculateDetailedFodmapSideScale,
  getFoodStackableSubtypes,
  matchIngredientToFodmap,
} from './fodmapEngine';
import { localStore, isTenScaleSymptom } from './storage';

// Free-text words that marked a flare in check-ins from before the 0–10 sliders.
const LEGACY_FLARE_TAGS = /sharp|cramp|reflux|distension|urgency|nausea|severe|pain|vomit/i;

/**
 * Bowel-movement logs store placeholder bloating/pain values derived from the stool type,
 * not something the user rated. They count as stool events only.
 */
export function isStoolOnlyEntry(s: Symptom): boolean {
  return s.stool_type !== null && s.stool_type !== undefined && /^\s*bowel movement/i.test(s.symptom_tags || '');
}

export function hasAbnormalStool(s: Symptom): boolean {
  return s.stool_type !== null && s.stool_type !== undefined && (s.stool_type <= 2 || s.stool_type >= 6);
}

/**
 * Single definition of a flare, used across the engine.
 * Rated symptoms must reach 5/10 (and 2 points above the person's resting level for bloating/pain);
 * mild ratings like "Stomach Pain (1/10)" no longer count.
 */
export function isFlareSymptom(
  s: Symptom,
  baseline?: { restingBloat: number; restingSoreness: number }
): boolean {
  if (hasAbnormalStool(s)) return true;
  if (isStoolOnlyEntry(s)) return false;

  const bloatThreshold = Math.max(5, (baseline?.restingBloat ?? 2) + 2);
  const soreThreshold = Math.max(5, (baseline?.restingSoreness ?? 2) + 2);
  if (s.bloating_score >= bloatThreshold || s.soreness_score >= soreThreshold) return true;

  const scores = s.symptom_scores || {};
  for (const [id, value] of Object.entries(scores)) {
    if (id === 'bloating' || id === 'stomach_pain') continue;
    if (value >= 5) return true;
  }
  if (s.bad_taste_score !== null && s.bad_taste_score !== undefined && s.bad_taste_score >= 5) return true;

  return !isTenScaleSymptom(s) && LEGACY_FLARE_TAGS.test(s.symptom_tags || '');
}

export function getTransitWindowConfig(speed: TransitSpeed = 'Normal'): TransitWindowConfig {
  switch (speed) {
    case 'Fast':
      return {
        speed: 'Fast',
        acuteMaxHours: 2,
        smallBowelMaxHours: 4,
        colonicMaxHours: 14,
        delayedImmuneMaxHours: 48,
        label: 'Fast / Rapid Transit (IBS-D Profile)',
      };
    case 'Slow':
      return {
        speed: 'Slow',
        acuteMaxHours: 4,
        smallBowelMaxHours: 8,
        colonicMaxHours: 36,
        delayedImmuneMaxHours: 72,
        label: 'Delayed / Slow Transit (IBS-C Profile)',
      };
    case 'Sluggish':
      return {
        speed: 'Sluggish',
        acuteMaxHours: 5,
        smallBowelMaxHours: 10,
        colonicMaxHours: 48,
        delayedImmuneMaxHours: 96,
        label: 'Sluggish / Severe Delayed Colonic (IBS-C)',
      };
    case 'Normal':
    default:
      return {
        speed: 'Normal',
        acuteMaxHours: 3,
        smallBowelMaxHours: 6,
        colonicMaxHours: 24,
        delayedImmuneMaxHours: 72,
        label: 'Standard Baseline Transit',
      };
  }
}

export function inferTransitSpeedFromSymptoms(
  symptoms: Symptom[],
  defaultSpeed: TransitSpeed = 'Normal'
): TransitSpeed {
  if (!symptoms || symptoms.length === 0) return defaultSpeed;

  const validStools = symptoms
    .map((s) => s.stool_type)
    .filter((st): st is number => typeof st === 'number' && st >= 1 && st <= 7);

  if (validStools.length >= 2) {
    const avgStool = validStools.reduce((sum, val) => sum + val, 0) / validStools.length;
    if (avgStool >= 5.3) return 'Fast';
    if (avgStool <= 2.2) return 'Sluggish';
    if (avgStool <= 2.8) return 'Slow';
    return 'Normal';
  }

  // Fallback to diarrhea vs constipation counts in symptom scores
  let diarrheaCount = 0;
  let constipationCount = 0;
  for (const s of symptoms) {
    const dScore = s.symptom_scores?.diarrhea ?? 0;
    const cScore = s.symptom_scores?.constipation ?? 0;
    if (dScore >= 3) diarrheaCount++;
    if (cScore >= 3) constipationCount++;
  }

  if (diarrheaCount > constipationCount && diarrheaCount >= 2) return 'Fast';
  if (constipationCount > diarrheaCount && constipationCount >= 2) return 'Slow';

  return defaultSpeed;
}

export interface ClinicalCategoryInfo {
  category: ClinicalFoodCategory;
  minHours: number;
  maxHours: number;
  peakHours: number;
  lagLabel: string;
  mechanism: string;
}

export function getClinicalCategory(tag: string, speed: TransitSpeed = 'Normal'): ClinicalCategoryInfo {
  const lower = tag.toLowerCase();
  const cfg = getTransitWindowConfig(speed);
  const factor = speed === 'Fast' ? 0.65 : speed === 'Slow' ? 1.4 : speed === 'Sluggish' ? 1.8 : 1.0;

  // Safe substitutes / Pure Lipids / Certified Safe parts / Free-From products (General digestion)
  if (
    /garlic[- ]infused|infused.*garlic|garlic.*oil|olive oil|spring onion green|scallion green|green tops|chive|chives|leek green|oyster mushroom|lactose[- ]free|lactose free|dairy[- ]free|gluten[- ]free|gluten free|\bgf\b/i.test(
      lower
    )
  ) {
    return {
      category: 'General',
      minHours: 2,
      maxHours: Math.round(cfg.colonicMaxHours * 1.5),
      peakHours: Math.round(cfg.colonicMaxHours * 0.5),
      lagLabel: `2–${Math.round(cfg.colonicMaxHours * 1.5)}h`,
      mechanism: 'Certified Low-FODMAP / safe metabolic digestion',
    };
  }

  // 1. Lactose & Dairy Sugars: Rapid enzyme deficiency, gas, acute loose stools (0.5h - 3h)
  // Strictly exclude anything marked lactose-free, dairy-free, or plant-based milks
  const isLactoseFreeProduct = /lactose[- ]free|lactose free|dairy[- ]free|plant[- ]based|vegan|(oat|almond|soy|soya|coconut|rice|cashew|plant)\s*milk/i.test(lower);
  if (
    !isLactoseFreeProduct &&
    /dairy|milk|lactose|cheese|cheddar|mozzarella|parmesan|feta|brie|gouda|cream|butter|ghee|yogurt|yoghurt|whey|ice cream|gelato|custard/i.test(
      lower
    )
  ) {
    return {
      category: 'Lactose',
      minHours: 0.5,
      maxHours: 3,
      peakHours: 1.5,
      lagLabel: '0.5–3h',
      mechanism: 'Lactase enzyme deficiency, acute osmotic flux',
    };
  }

  // 2. Histamines & Stimulants: Flushing, tachycardia, rapid transit (0.5h - 4h)
  if (
    /histamine|coffee|espresso|caffeine|wine|beer|alcohol|salami|prosciutto|aged cheese|fermented|sauerkraut|kimchi|vinegar|kombucha/i.test(
      lower
    )
  ) {
    return {
      category: 'Histamine',
      minHours: 0.5,
      maxHours: 4,
      peakHours: 2,
      lagLabel: '0.5–4h',
      mechanism: 'Amine overload & DAO enzyme saturation',
    };
  }

  // 3. FODMAPs & Alliums: Large bowel bacterial fermentation (4h - 24h, next-morning distension)
  if (
    /fodmap|garlic|onion|shallot|allium|leek|scallion|beans|kidney bean|black bean|baked bean|lentil|chickpea|hummus|apple|pear|mango|mushroom|cauliflower|cabbage|broccoli/i.test(
      lower
    )
  ) {
    return {
      category: 'FODMAP',
      minHours: 4,
      maxHours: 24,
      peakHours: 14,
      lagLabel: '4–24h',
      mechanism: 'Colonic bacterial fermentation & distension gas',
    };
  }

  // 4. Gluten & Grains: Non-coeliac delayed immune reaction (12h - 48h)
  // Strictly exclude gluten-free flours, breads, and grains
  const isGlutenFreeGrainOrFlour =
    /(rice|corn|almond|coconut|tapioca|cassava|arrowroot|potato|buckwheat|oat|sorghum|millet|teff|chickpea|maize|gluten[- ]free|gluten free|\bgf\b)\s*(flour|bread|pasta|noodles?|grain)?/i.test(
      lower
    );

  if (
    !isGlutenFreeGrainOrFlour &&
    !/gluten[- ]free|gluten free|\bgf\b|wheat[- ]free/i.test(lower) &&
    /\b(gluten|wheat|wheat flour|plain flour|all-purpose flour|self-raising flour|sourdough|bread|toast|bagel|croissant|brioche|pasta|spaghetti|noodles|ramen|pizza|barley|rye|spelt|couscous|semolina|seitan)\b/i.test(
      lower
    )
  ) {
    return {
      category: 'Gluten',
      minHours: 12,
      maxHours: 48,
      peakHours: 24,
      lagLabel: '12–48h',
      mechanism: 'Delayed immune/inflammatory mucosal response',
    };
  }

  // 5. Plain Meats & Animal Proteins (2h - 24h)
  if (
    /beef|steak|brisket|mince|ground beef|chicken|poultry|turkey|pork|bacon|ham|lamb|duck|veal|venison|salmon|tuna|cod|fish|prawn|shrimp/i.test(
      lower
    )
  ) {
    return {
      category: 'Protein',
      minHours: 2,
      maxHours: 24,
      peakHours: 6,
      lagLabel: '2–24h',
      mechanism: 'Gastric peptic breakdown & upper intestinal proteolysis',
    };
  }

  // 6. General Foods & Lipids (2h - 48h)
  return {
    category: 'General',
    minHours: 2,
    maxHours: 48,
    peakHours: 12,
    lagLabel: '2–48h',
    mechanism: 'Gastric emptying & metabolic digestion transit',
  };
}

export function calculateCategoryRelevanceWeight(tag: string, lagHours: number): number {
  const cat = getClinicalCategory(tag);
  if (lagHours >= cat.minHours && lagHours <= cat.maxHours) {
    return 1.0; // High relevance (primary category window)
  }
  if (lagHours >= 0.5 && lagHours <= cat.maxHours * 1.5) {
    return 0.5; // Moderate relevance (standard digestion window)
  }
  if (lagHours > cat.maxHours * 1.5 && lagHours <= 72) {
    return 0.1; // Extended Anomaly window
  }
  return 0;
}

export interface IngredientStat {
  tag: string;
  totalOccurrences: number;
  followedByBloatCount: number; // bloating_score >= 3
  followedBySorenessCount: number; // soreness_score >= 3
  avgBloatingImpact: number;
  avgSorenessImpact: number;
  highestLagHours: number[];
  riskScore: number; // 0 to 100
}

export function getTransitPhase(lagHours: number, speed: TransitSpeed = 'Normal'): TransitPhase {
  const cfg = getTransitWindowConfig(speed);
  if (lagHours < cfg.acuteMaxHours) return 'Acute';
  if (lagHours <= cfg.smallBowelMaxHours) return 'Small Bowel';
  if (lagHours <= cfg.colonicMaxHours) return 'Colonic';
  return 'Delayed Immune';
}

export function getTransitPhaseLabel(lagHours: number, speed: TransitSpeed = 'Normal'): string {
  if (lagHours <= 0) return 'Tolerated Baseline';
  const cfg = getTransitWindowConfig(speed);
  if (lagHours < cfg.acuteMaxHours) return `Acute Upper GI (0–${cfg.acuteMaxHours}h)`;
  if (lagHours <= cfg.smallBowelMaxHours) return `Small Bowel Phase (${cfg.acuteMaxHours}–${cfg.smallBowelMaxHours}h)`;
  if (lagHours <= cfg.colonicMaxHours) return `Colonic Fermentation (${cfg.smallBowelMaxHours}–${cfg.colonicMaxHours}h)`;
  return `Delayed Immune Reaction (${cfg.colonicMaxHours}–${cfg.delayedImmuneMaxHours}h)`;
}

export function buildTimelineCorrelations(
  meals: Meal[],
  symptoms: Symptom[],
  speed: TransitSpeed = 'Normal'
): CorrelationMatch[] {
  const cfg = getTransitWindowConfig(speed);
  // Sort descending by timestamp
  const sortedSymptoms = [...symptoms].sort(
    (a, b) => new Date(b.timestamp_iso).getTime() - new Date(a.timestamp_iso).getTime()
  );

  return sortedSymptoms.map((symptom) => {
    const sTime = new Date(symptom.timestamp_iso).getTime();

    // Find meals consumed prior (scaled by user's personal transit profile)
    const precedingMeals = meals
      .filter((meal) => {
        // If symptom was explicitly linked to a meal, strictly only link to that meal!
        if (symptom.associated_meal_id) {
          return symptom.associated_meal_id === meal.id;
        }
        const mTime = new Date(meal.timestamp_iso).getTime();
        const lagHours = (sTime - mTime) / (1000 * 60 * 60);
        return lagHours >= 0.5 && lagHours <= cfg.delayedImmuneMaxHours;
      })
      .map((meal) => {
        const lagHours = calculateLagHours(meal.timestamp_iso, symptom.timestamp_iso);
        const isNextMorningLag =
          (symptom.checkin_type === 'Next-Morning' ||
            (new Date(symptom.timestamp_iso).getHours() >= 6 &&
              new Date(symptom.timestamp_iso).getHours() <= 11)) &&
          (meal.meal_type === 'Dinner' || meal.meal_type === 'Snack') &&
          lagHours >= (speed === 'Fast' ? 5 : 7) &&
          lagHours <= (speed === 'Fast' ? 12 : speed === 'Slow' ? 20 : 16);
        const transitPhase = getTransitPhase(lagHours, speed);
        const isDelayedImmuneLag = lagHours > cfg.colonicMaxHours && lagHours <= cfg.delayedImmuneMaxHours;

        return {
          meal,
          lagHours,
          isNextMorningLag,
          transitPhase,
          isDelayedImmuneLag,
        };
      })
      .sort((a, b) => a.lagHours - b.lagHours);

    return {
      symptom,
      precedingMeals,
    };
  });
}

// Common food trigger dictionary for parsing descriptions
const COMMON_FOOD_PATTERNS: { pattern: RegExp; tag: string }[] = [
  // Alliums
  { pattern: /\bgarlic\b/i, tag: 'garlic' },
  { pattern: /\bonions?\b/i, tag: 'onion' },
  { pattern: /\bshallots?\b/i, tag: 'shallot' },
  { pattern: /\b(scallions?|spring onions?|leeks?|chives?)\b/i, tag: 'allium' },

  // Dairy & Lactose
  { pattern: /\b(dairy|milk|lactose)\b/i, tag: 'dairy' },
  { pattern: /\b(cheese|parmesan|cheddar|mozzarella|feta|brie)\b/i, tag: 'cheese' },
  { pattern: /\b(cream|creamy|half and half)\b/i, tag: 'cream' },
  { pattern: /\b(butter|ghee)\b/i, tag: 'butter' },
  { pattern: /\b(yogurt|yoghurt|greek yogurt)\b/i, tag: 'yogurt' },
  { pattern: /\b(ice cream|gelato)\b/i, tag: 'ice cream' },

  // Gluten & Wheat Grains
  { pattern: /\bgluten\b/i, tag: 'gluten' },
  { pattern: /\b(wheat|flour)\b/i, tag: 'wheat' },
  { pattern: /\b(sourdough|bread|toast|bagel|croissant|brioche)\b/i, tag: 'bread' },
  { pattern: /\b(pasta|spaghetti|fettuccine|penne|linguine|macaroni|noodles?|ramen)\b/i, tag: 'pasta' },
  { pattern: /\bpizza\b/i, tag: 'pizza' },
  { pattern: /\b(oats|oatmeal|porridge)\b/i, tag: 'oats' },
  { pattern: /\b(rice|jasmine rice|basmati|brown rice)\b/i, tag: 'rice' },
  { pattern: /\bquinoa\b/i, tag: 'quinoa' },

  // Proteins
  { pattern: /\b(chicken|poultry)\b/i, tag: 'chicken' },
  { pattern: /\b(beef|steak|burger)\b/i, tag: 'beef' },
  { pattern: /\b(pork|bacon|ham|sausage)\b/i, tag: 'pork' },
  { pattern: /\b(salmon|tuna|cod|trout|fish|seafood)\b/i, tag: 'fish' },
  { pattern: /\b(shrimp|prawns?|crab|lobster|shellfish)\b/i, tag: 'shellfish' },
  { pattern: /\beggs?\b/i, tag: 'eggs' },
  { pattern: /\b(tofu|tempeh|edamame|soy|soya)\b/i, tag: 'soy' },

  // Legumes & FODMAPs
  { pattern: /\b(beans?|black beans?|kidney beans?|baked beans?)\b/i, tag: 'beans' },
  { pattern: /\b(lentils?|dal|dhal)\b/i, tag: 'lentils' },
  { pattern: /\b(chickpeas?|garbanzo|hummus)\b/i, tag: 'chickpeas' },
  { pattern: /\bfodmaps?\b/i, tag: 'high fodmap' },

  // Nightshades
  { pattern: /\b(tomatoes?|tomato sauce|marinara)\b/i, tag: 'tomato' },
  { pattern: /\b(potatoes?|fries|chips|mash)\b/i, tag: 'potato' },
  { pattern: /\b(peppers?|bell peppers?|capsicum)\b/i, tag: 'bell pepper' },
  { pattern: /\b(eggplant|aubergine)\b/i, tag: 'eggplant' },

  // Nuts & Seeds
  { pattern: /\b(peanuts?|peanut butter)\b/i, tag: 'peanut' },
  { pattern: /\b(almonds?|almond milk|almond butter)\b/i, tag: 'almond' },
  { pattern: /\b(walnuts?|cashews?|pecans?|pistachios?|nuts?)\b/i, tag: 'nuts' },
  { pattern: /\b(chia|flax|sesame)\b/i, tag: 'seeds' },

  // Beverages & Stimulants
  { pattern: /\b(coffee|espresso|cappuccino|latte|americano|cold brew)\b/i, tag: 'coffee' },
  { pattern: /\bcaffeine\b/i, tag: 'caffeine' },
  { pattern: /\b(tea|green tea|black tea|matcha)\b/i, tag: 'tea' },
  { pattern: /\b(alcohol|wine|beer|cocktail|cider|spirits?|whiskey|vodka)\b/i, tag: 'alcohol' },

  // Spices & Sensitivities
  { pattern: /\b(spicy|chili|jalapeno|sriracha|hot sauce|habanero|cayenne)\b/i, tag: 'spicy' },
  { pattern: /\bcurry\b/i, tag: 'curry' },
  { pattern: /\b(avocado|guacamole)\b/i, tag: 'avocado' },
  { pattern: /\b(chocolate|cocoa)\b/i, tag: 'chocolate' },
];

// Filler ingredients that are not clinical food intolerances
const FILLER_INGREDIENTS = new Set([
  'water', 'salt', 'sugar', 'ice', 'citric acid', 'flavouring', 'flavoring',
  'natural flavouring', 'natural flavoring', 'extract', 'yeast', 'vinegar',
  'spirit vinegar', 'cider vinegar', 'white wine vinegar', 'rice vinegar',
  'molasses', 'cornflour', 'tapioca starch', 'starch', 'bicarbonate',
  'sodium bicarbonate', 'glucose syrup', 'golden syrup', 'invert sugar syrup',
  'stabilisers', 'locust bean gum', 'guar gum', 'emulsifier', 'citrus fibre'
]);

const EXTRACT_FOODS_CACHE = new Map<string, string[]>();

export function extractFoodsFromMeal(meal: Partial<Meal>): string[] {
  if (!meal) return [];
  const key = `${meal.id || ''}:${meal.timestamp_iso || ''}:${meal.ingredients_tags || ''}:${meal.anti_aggravates || ''}:${meal.description || ''}`;
  const cached = EXTRACT_FOODS_CACHE.get(key);
  if (cached) return cached;

  const result = _doExtractFoodsFromMeal(meal);
  if (EXTRACT_FOODS_CACHE.size > 2000) {
    EXTRACT_FOODS_CACHE.clear();
  }
  EXTRACT_FOODS_CACHE.set(key, result);
  return result;
}

function _doExtractFoodsFromMeal(meal: Partial<Meal>): string[] {
  const result = new Set<string>();

  // Load user corrections for personalized blacklisting and aliasing
  const userCorrections = typeof window !== 'undefined' ? localStore.getUserFoodCorrections() : {};

  // 1. Build strict allergen exclusions set from anti_aggravates and negative qualifiers
  const excludedAllergens = new Set<string>();
  if (meal.anti_aggravates) {
    meal.anti_aggravates
      .split(',')
      .map((a) => a.trim().toLowerCase())
      .filter(Boolean)
      .forEach((a) => {
        excludedAllergens.add(a);
        if (a === 'gluten' || a === 'wheat') {
          excludedAllergens.add('gluten');
          excludedAllergens.add('wheat');
          excludedAllergens.add('bread');
          excludedAllergens.add('pasta');
        }
        if (a === 'dairy' || a === 'lactose') {
          excludedAllergens.add('dairy');
          excludedAllergens.add('lactose');
          excludedAllergens.add('milk');
          excludedAllergens.add('cheese');
          excludedAllergens.add('cream');
          excludedAllergens.add('butter');
        }
        if (a === 'allium' || a === 'garlic' || a === 'onion') {
          excludedAllergens.add('garlic');
          excludedAllergens.add('onion');
          excludedAllergens.add('allium');
        }
      });
  }

  // Scan description and tags for free-from keywords
  const combinedText = `${meal.description || ''} ${meal.ingredients_tags || ''}`.toLowerCase();
  if (/gluten[- ]free|wheat[- ]free|\bgf\b|without gluten|no gluten/i.test(combinedText)) {
    excludedAllergens.add('gluten');
    excludedAllergens.add('wheat');
    excludedAllergens.add('bread');
    excludedAllergens.add('toast');
    excludedAllergens.add('pasta');
    excludedAllergens.add('noodle');
    excludedAllergens.add('noodles');
    excludedAllergens.add('flour');
    excludedAllergens.add('wheat flour');
    excludedAllergens.add('croissant');
    excludedAllergens.add('brioche');
    excludedAllergens.add('bagel');
    excludedAllergens.add('couscous');
    excludedAllergens.add('semolina');
    excludedAllergens.add('spelt');
    excludedAllergens.add('rye');
    excludedAllergens.add('barley');
  }
  if (/dairy[- ]free|lactose[- ]free|non[- ]dairy|without dairy|no dairy/i.test(combinedText)) {
    excludedAllergens.add('dairy');
    excludedAllergens.add('lactose');
    excludedAllergens.add('milk');
    excludedAllergens.add('cow');
    excludedAllergens.add('cheese');
    excludedAllergens.add('cream');
    excludedAllergens.add('butter');
    excludedAllergens.add('yogurt');
    excludedAllergens.add('yoghurt');
    excludedAllergens.add('whey');
    excludedAllergens.add('ice cream');
  }
  if (/allium[- ]free|garlic[- ]free|onion[- ]free|without garlic|no garlic|no onion/i.test(combinedText)) {
    excludedAllergens.add('garlic');
    excludedAllergens.add('onion');
    excludedAllergens.add('allium');
  }
  if (/nut[- ]free|peanut[- ]free/i.test(combinedText)) {
    excludedAllergens.add('nuts');
    excludedAllergens.add('peanuts');
  }
  if (/egg[- ]free/i.test(combinedText)) {
    excludedAllergens.add('eggs');
    excludedAllergens.add('egg');
  }
  if (/soy[- ]free|soya[- ]free/i.test(combinedText)) {
    excludedAllergens.add('soy');
  }

  const isGlutenCarrier = (f: string) =>
    /^(gluten|wheat|bread|toast|pasta|noodles?|flour|wheat flour|croissant|brioche|bagel|pizza|couscous|semolina|spelt|rye|barley)$/i.test(f);

  const isDairyCarrier = (f: string) =>
    /^(dairy|milk|cow|cheese|cheddar|mozzarella|parmesan|feta|brie|cream|butter|ghee|yogurt|yoghurt|whey|ice cream)$/i.test(f);

  const addFoodSafely = (rawToken: string, forceGlutenFree = false, forceLactoseFree = false) => {
    let cleaned = rawToken.trim().toLowerCase();
    if (!cleaned || DESCRIPTOR_STOPWORDS.has(cleaned) || FILLER_INGREDIENTS.has(cleaned)) return;

    // Check user corrections
    const userCorr = userCorrections[cleaned];
    if (userCorr) {
      if (userCorr.action === 'blacklist_not_food') return;
      if (userCorr.action === 'alias' && userCorr.targetAlias) {
        cleaned = userCorr.targetAlias.toLowerCase();
      }
      if (userCorr.action === 'free_from_override') {
        if (userCorr.freeFromType === 'gluten-free') forceGlutenFree = true;
        if (userCorr.freeFromType === 'lactose-free' || userCorr.freeFromType === 'dairy-free') forceLactoseFree = true;
      }
    }

    const norm = normalizeFoodDatabaseTitle(cleaned);
    const candidate = norm.displayName || cleaned;
    const candidateLower = candidate.toLowerCase();
    if (DESCRIPTOR_STOPWORDS.has(candidateLower) || FILLER_INGREDIENTS.has(candidateLower)) return;

    const isGf = forceGlutenFree || norm.isGlutenFree || /gluten[- ]free|\bgf\b/i.test(candidateLower);
    const isLf = forceLactoseFree || norm.isLactoseFree || norm.isDairyFree || /lactose[- ]free|dairy[- ]free/i.test(candidateLower);

    // Guard gluten exclusions
    if ((excludedAllergens.has('gluten') || isGf) && isGlutenCarrier(candidateLower)) {
      return;
    }
    // Guard dairy exclusions
    if ((excludedAllergens.has('dairy') || isLf) && isDairyCarrier(candidateLower)) {
      return;
    }

    result.add(candidateLower);

    // Roll up to core allergen families ONLY if not excluded and not free-from
    if (candidateLower.includes('soy') && !excludedAllergens.has('soy') && !candidateLower.includes('free')) {
      result.add('soy');
    }
    if (
      !isLf &&
      !excludedAllergens.has('dairy') &&
      !candidateLower.includes('free') &&
      !/(oat|almond|soy|soya|coconut|rice|cashew|plant)\s*milk/i.test(candidateLower) &&
      (candidateLower.includes('milk') || candidateLower.includes('dairy') || candidateLower.includes('cheese') || candidateLower.includes('cream'))
    ) {
      result.add('dairy');
    }
    const isGfFlourOrGrain =
      /(rice|corn|almond|coconut|tapioca|cassava|arrowroot|potato|buckwheat|oat|sorghum|millet|teff|chickpea|maize|gluten[- ]free|gf)\s*(flour|bread|pasta|noodles?|grain)?/i.test(
        candidateLower
      );
    if (
      !isGf &&
      !isGfFlourOrGrain &&
      !excludedAllergens.has('gluten') &&
      !candidateLower.includes('free') &&
      (candidateLower.includes('gluten') ||
        candidateLower.includes('wheat') ||
        candidateLower.includes('bread') ||
        candidateLower.includes('toast') ||
        candidateLower.includes('pasta') ||
        candidateLower.includes('noodle') ||
        candidateLower.includes('sourdough') ||
        candidateLower.includes('brioche') ||
        /\b(wheat\s*flour|plain\s*flour|all\s*purpose\s*flour|flour)\b/i.test(candidateLower))
    ) {
      result.add('gluten');
    }
  };

  // 2. Prioritize structured ingredients if present on meal
  if (meal.ingredients && Array.isArray(meal.ingredients) && meal.ingredients.length > 0) {
    for (const item of meal.ingredients) {
      const name = item.canonicalName || item.name;
      if (name) {
        const norm = normalizeFoodDatabaseTitle(name);
        for (const c of norm.constituents) {
          addFoodSafely(c, norm.isGlutenFree, norm.isLactoseFree);
        }
      }
    }
  }

  // 3. Process explicit ingredients_tags with inverted title normalizer
  if (meal.ingredients_tags) {
    // If the tags contain commas, first check if entire segments are inverted food titles
    // e.g. "Cucumber, continental, raw"
    const wholeNormalized = normalizeFoodDatabaseTitle(meal.ingredients_tags);
    if (wholeNormalized.constituents.length === 1 && wholeNormalized.displayName !== meal.ingredients_tags) {
      addFoodSafely(wholeNormalized.displayName, wholeNormalized.isGlutenFree, wholeNormalized.isLactoseFree);
    } else {
      const rawTags = meal.ingredients_tags
        .split(/[,;\n]/)
        .map((t) => t.replace(/^[^\w]+|[^\w]+$/g, '').trim())
        .filter(Boolean);

      for (const raw of rawTags) {
        addFoodSafely(raw);
      }
    }
  }

  // 4. Parse description with patterns (respecting exclusions)
  const descText = (meal.description || '').toLowerCase();

  for (const { pattern, tag } of COMMON_FOOD_PATTERNS) {
    if (pattern.test(descText)) {
      if (excludedAllergens.has(tag)) continue;

      // Check negative qualifiers in description
      const isNegative = new RegExp(`(gluten[- ]free|dairy[- ]free|no|without|vegan|plant[- ]based)\\s+${tag}`, 'i').test(descText);
      if (isNegative) continue;

      addFoodSafely(tag);
    }
  }

  // 5. Extract key standalone food phrases from description
  const segments = descText
    .split(/,|\band\b|\bwith\b|\b&\b|\+/i)
    .map((s) => s.trim())
    .filter((s) => s.length >= 3 && s.length <= 25);

  const STOPWORDS = new Set([
    'meal', 'heavy', 'rich', 'quick', 'snack', 'breakfast', 'lunch', 'dinner',
    'plate', 'bowl', 'dish', 'food', 'felt', 'hungry', 'eating', 'large', 'small',
    'portion', 'tasty', 'delicious', 'light', 'full', 'late'
  ]);

  for (const seg of segments) {
    const cleaned = seg
      .replace(/^(grilled|baked|fried|steamed|roasted|fresh|warm|hot|cold|raw|cooked|organic|plain|boiled|diced|sliced|chopped|minced|peeled|unpeeled|skinless|boneless|lean|shredded|common|firm|ripe|unripe|standard|generic|continental)\s+/i, '')
      .replace(/\s+(meal|dish|plate|snack|portion|all cuts|cuts|peeled|regular fat|low fat|full fat|fat free|common|firm|ripe|unripe|standard|generic|continental)$/i, '')
      .trim();

    if (
      cleaned.length >= 3 &&
      !STOPWORDS.has(cleaned) &&
      !DESCRIPTOR_STOPWORDS.has(cleaned) &&
      !cleaned.includes('ate ') &&
      !cleaned.includes('felt ') &&
      !cleaned.includes('free') &&
      !FILLER_INGREDIENTS.has(cleaned)
    ) {
      addFoodSafely(cleaned);
    }
  }

  return Array.from(result);
}

export interface GutBaselineInfo {
  restingBloat: number;
  restingSoreness: number;
  restingWellbeing: number;
  isCalibrating: boolean;
  daysLogged: number;
  totalMeals: number;
  totalSymptoms: number;
}

export function calculateGutBaseline(
  meals: Meal[],
  symptoms: Symptom[],
  customSettings?: { autoCalculate: boolean; restingBloat: number; restingSoreness: number; restingWellbeing: number }
): GutBaselineInfo {
  const dates = new Set<string>();
  meals.forEach((m) => dates.add(m.date));
  symptoms.forEach((s) => dates.add(s.date));
  const daysLogged = Math.max(1, dates.size);
  const isCalibrating = daysLogged < 7 || meals.length < 20;

  if (customSettings && !customSettings.autoCalculate) {
    return {
      restingBloat: customSettings.restingBloat,
      restingSoreness: customSettings.restingSoreness,
      restingWellbeing: customSettings.restingWellbeing,
      isCalibrating,
      daysLogged,
      totalMeals: meals.length,
      totalSymptoms: symptoms.length,
    };
  }

  // Bowel-movement logs carry placeholder scores, so the resting level comes from rated check-ins only.
  const rated = symptoms.filter((s) => !isStoolOnlyEntry(s));

  const avgBloat =
    rated.length > 0
      ? Math.round((rated.reduce((acc, s) => acc + s.bloating_score, 0) / rated.length) * 10) / 10
      : 2.0;

  const avgSore =
    rated.length > 0
      ? Math.round((rated.reduce((acc, s) => acc + s.soreness_score, 0) / rated.length) * 10) / 10
      : 2.0;

  const avgWellbeing =
    rated.length > 0
      ? Math.round((rated.reduce((acc, s) => acc + s.overall_wellbeing, 0) / rated.length) * 10) / 10
      : 3.0;

  return {
    restingBloat: avgBloat,
    restingSoreness: avgSore,
    restingWellbeing: avgWellbeing,
    isCalibrating,
    daysLogged,
    totalMeals: meals.length,
    totalSymptoms: symptoms.length,
  };
}

export function computeIngredientStatistics(
  meals: Meal[],
  symptoms: Symptom[],
  customBaseline?: { autoCalculate: boolean; restingBloat: number; restingSoreness: number; restingWellbeing: number }
): {
  suspectedTriggers: IngredientStat[];
  safeFoods: IngredientStat[];
} {
  const baseline = calculateGutBaseline(meals, symptoms, customBaseline);
  const matches = buildTimelineCorrelations(meals, symptoms);

  // Map of meal ID to following symptoms
  const mealImpactMap = new Map<string, {
    maxBloat: number;
    maxSore: number;
    hasSpike: boolean;
    lags: number[];
  }>();

  for (const match of matches) {
    const s = match.symptom;
    const isSpike = isFlareSymptom(s);

    for (const p of match.precedingMeals) {
      let mData = mealImpactMap.get(p.meal.id);
      if (!mData) {
        mData = { maxBloat: s.bloating_score, maxSore: s.soreness_score, hasSpike: isSpike, lags: [p.lagHours] };
        mealImpactMap.set(p.meal.id, mData);
      } else {
        mData.maxBloat = Math.max(mData.maxBloat, s.bloating_score);
        mData.maxSore = Math.max(mData.maxSore, s.soreness_score);
        if (isSpike) mData.hasSpike = true;
        mData.lags.push(p.lagHours);
      }
    }
  }

  // Aggregate by ingredient tag across discrete meal exposures
  const tagMap = new Map<string, {
    totalOccurrences: number;
    bloatSum: number;
    sorenessSum: number;
    spikesCount: number;
    lags: number[];
  }>();

  for (const meal of meals) {
    const tags = extractFoodsFromMeal(meal);
    const mData = mealImpactMap.get(meal.id);

    for (const tag of tags) {
      let entry = tagMap.get(tag);
      if (!entry) {
        entry = {
          totalOccurrences: 0,
          bloatSum: 0,
          sorenessSum: 0,
          spikesCount: 0,
          lags: [],
        };
        tagMap.set(tag, entry);
      }
      entry.totalOccurrences += 1;
      if (mData) {
        entry.bloatSum += mData.maxBloat;
        entry.sorenessSum += mData.maxSore;
        if (mData.hasSpike) entry.spikesCount += 1;
        entry.lags.push(...mData.lags);
      } else {
        entry.bloatSum += baseline.restingBloat;
        entry.sorenessSum += baseline.restingSoreness;
      }
    }
  }

  const allStats: IngredientStat[] = [];
  tagMap.forEach((data, tag) => {
    const avgBloat = data.totalOccurrences > 0 ? data.bloatSum / data.totalOccurrences : baseline.restingBloat;
    const avgSore = data.totalOccurrences > 0 ? data.sorenessSum / data.totalOccurrences : baseline.restingSoreness;
    const spikeRate = data.spikesCount / Math.max(1, data.totalOccurrences);
    const riskScore = Math.min(100, Math.round(spikeRate * 70 + ((avgBloat + avgSore) / 10) * 30));

    allStats.push({
      tag,
      totalOccurrences: data.totalOccurrences,
      followedByBloatCount: data.spikesCount,
      followedBySorenessCount: data.spikesCount,
      avgBloatingImpact: Math.round(avgBloat * 10) / 10,
      avgSorenessImpact: Math.round(avgSore * 10) / 10,
      highestLagHours: data.lags,
      riskScore,
    });
  });

  // Only consider as suspected triggers if accompanied by repeat discomfort spikes (N >= 2)
  // and exclude zero-FODMAP staples from taking composite meal blame
  const suspectedTriggers = allStats
    .filter((s) => {
      const canonical = canonicalizeIngredient(s.tag);
      if (isZeroFodmapFood(canonical) || isZeroFodmapFood(s.tag)) return false;
      const reactionRate = s.followedByBloatCount / Math.max(1, s.totalOccurrences);
      return (
        (s.followedByBloatCount >= 2 && reactionRate >= 0.50 && s.riskScore >= 50) ||
        (s.followedByBloatCount >= 3 && reactionRate >= 0.40 && s.riskScore >= 45)
      );
    })
    .sort((a, b) => b.riskScore - a.riskScore || b.followedByBloatCount - a.followedByBloatCount);

  // Safe foods: eaten multiple times without acute spikes above baseline or certified staples
  const safeFoods = allStats
    .filter((s) => {
      const canonical = canonicalizeIngredient(s.tag);
      const isStaple = isZeroFodmapFood(canonical) || isZeroFodmapFood(s.tag);
      if (isStaple && s.totalOccurrences >= 1 && s.riskScore < 40) return true;
      return s.totalOccurrences >= 2 && s.followedByBloatCount === 0 && s.riskScore < 35;
    })
    .sort((a, b) => b.totalOccurrences - a.totalOccurrences);

  return { suspectedTriggers, safeFoods };
}

/**
 * Computes longitudinal multi-week intolerance persistence across all logged meals and symptoms.
 * Classifies ingredients into Persistent Culprits, Emerging Suspects, Watchlist, or Safe Baselines.
 */
export function computeLongitudinalIntoleranceStats(
  meals: Meal[],
  symptoms: Symptom[],
  customBaseline?: { autoCalculate: boolean; restingBloat: number; restingSoreness: number; restingWellbeing: number },
  speed: TransitSpeed = 'Normal'
): LongitudinalIntoleranceItem[] {
  const cfg = getTransitWindowConfig(speed);
  const baseline = calculateGutBaseline(meals, symptoms, customBaseline);
  const totalEatenMap = new Map<string, { count: number; lastDate: string }>();

  // Sort meals chronologically once
  const chronologicalMeals = [...meals].sort(
    (a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime()
  );

  // Pre-index food -> chronological meals list in O(M) single pass
  const mealsByFood = new Map<string, Meal[]>();

  // 1. Total occurrences of each food across all meals
  for (const meal of chronologicalMeals) {
    const foods = extractFoodsFromMeal(meal);
    for (const food of foods) {
      let list = mealsByFood.get(food);
      if (!list) {
        list = [];
        mealsByFood.set(food, list);
      }
      list.push(meal);

      const existing = totalEatenMap.get(food);
      if (!existing) {
        totalEatenMap.set(food, { count: 1, lastDate: meal.date });
      } else {
        existing.count += 1;
        if (new Date(meal.date).getTime() > new Date(existing.lastDate).getTime()) {
          existing.lastDate = meal.date;
        }
      }
    }
  }

  // 2. Identify discrete meals followed by acute spikes above resting baseline
  // Load user corrections for personalized blacklisting, aliasing, and de-confounding
  const userCorrections = typeof window !== 'undefined' ? localStore.getUserFoodCorrections() : {};

  const isPotentAggravator = (f: string) => {
    const lower = f.toLowerCase().trim();
    if (userCorrections[lower]?.action === 'mark_tolerated') return false;
    if (userCorrections[lower]?.action === 'confirm_trigger') return true;
    return /\b(garlic|onion|shallot|allium|leek|milk|dairy|lactose|cheese|cream|whey|chili|spicy|wheat|gluten|pasta|bread|coffee|espresso|alcohol|wine|beer|beans|kidney bean|baked bean|lentils|chickpeas)\b/i.test(lower);
  };

  const stoolSymptoms = symptoms.filter((s) => s.stool_type !== null && s.stool_type !== undefined);
  const evacuationDates = new Set(stoolSymptoms.map((s) => s.date));

  // Compute baseline meal flare statistics across the entire logged timeline
  let baselineAdverseMealsCount = 0;
  for (const meal of chronologicalMeals) {
    const mTime = new Date(meal.timestamp_iso).getTime();
    const hasMealSpike = symptoms.some((s) => {
      if (s.associated_meal_id) return s.associated_meal_id === meal.id;
      const sTime = new Date(s.timestamp_iso).getTime();
      const lag = (sTime - mTime) / (1000 * 60 * 60);
      if (lag < 0.5 || lag > cfg.colonicMaxHours) return false;
      return isFlareSymptom(s, baseline);
    });
    if (hasMealSpike) baselineAdverseMealsCount++;
  }
  const baselineFlareRate = chronologicalMeals.length > 0
    ? baselineAdverseMealsCount / chronologicalMeals.length
    : 0.25;

  // Map each food to its meal-by-meal impact and cross-referencing statistics
  const foodCrossReferenceMap = new Map<string, {
    totalOccurrences: number;
    adverseMeals: Meal[];
    calmMeals: Meal[];
    soloCalmCount: number;
    soloFlareCount: number;
    coOccurringFlareCount: number;
    motilityGapFlareCount: number;
    triggerDates: Set<string>;
    lags: number[];
    maxBloat: number;
    maxSoreness: number;
  }>();

  for (const meal of chronologicalMeals) {
    const foodsInMeal = extractFoodsFromMeal(meal);
    const mTime = new Date(meal.timestamp_iso).getTime();

    for (const food of foodsInMeal) {
      const catInfo = getClinicalCategory(food, speed);
      
      // Precision biological lag window check for this food
      const followingSymptomsInWindow = symptoms.filter((s) => {
        if (s.associated_meal_id) {
          return s.associated_meal_id === meal.id;
        }
        const sTime = new Date(s.timestamp_iso).getTime();
        const lag = (sTime - mTime) / (1000 * 60 * 60);
        return lag >= catInfo.minHours && lag <= catInfo.maxHours;
      });

      const spikeSymptoms = followingSymptomsInWindow.filter((s) => isFlareSymptom(s, baseline));

      const hasSpike = spikeSymptoms.length > 0;
      const otherFoods = foodsInMeal.filter((f) => f.toLowerCase() !== food.toLowerCase());
      const hasOtherAggravator = otherFoods.some((f) => isPotentAggravator(f));
      const isMotilityGap = !evacuationDates.has(meal.date) || (spikeSymptoms[0] && !evacuationDates.has(spikeSymptoms[0].date));

      let crEntry = foodCrossReferenceMap.get(food);
      if (!crEntry) {
        crEntry = {
          totalOccurrences: 0,
          adverseMeals: [],
          calmMeals: [],
          soloCalmCount: 0,
          soloFlareCount: 0,
          coOccurringFlareCount: 0,
          motilityGapFlareCount: 0,
          triggerDates: new Set<string>(),
          lags: [],
          maxBloat: 0,
          maxSoreness: 0,
        };
        foodCrossReferenceMap.set(food, crEntry);
      }

      crEntry.totalOccurrences += 1;

      if (hasSpike) {
        crEntry.adverseMeals.push(meal);
        crEntry.triggerDates.add(spikeSymptoms[0].date);
        const lags = spikeSymptoms.map((s) => calculateLagHours(meal.timestamp_iso, s.timestamp_iso));
        crEntry.lags.push(...lags);
        const rated = spikeSymptoms.filter((s) => !isStoolOnlyEntry(s));
        const maxB = Math.max(0, ...rated.map((s) => s.bloating_score));
        const maxS = Math.max(0, ...rated.map((s) => s.soreness_score));
        if (maxB > crEntry.maxBloat) crEntry.maxBloat = maxB;
        if (maxS > crEntry.maxSoreness) crEntry.maxSoreness = maxS;

        if (isMotilityGap) {
          crEntry.motilityGapFlareCount += 1;
        }

        if (hasOtherAggravator) {
          crEntry.coOccurringFlareCount += 1;
        } else {
          crEntry.soloFlareCount += 1;
        }
      } else {
        crEntry.calmMeals.push(meal);
        if (!hasOtherAggravator) {
          crEntry.soloCalmCount += 1;
        }
      }
    }
  }

  const results: LongitudinalIntoleranceItem[] = [];

  totalEatenMap.forEach((eatenData, tag) => {
    const tagLower = tag.toLowerCase().trim();
    if (!tag || DESCRIPTOR_STOPWORDS.has(tagLower) || FILLER_INGREDIENTS.has(tagLower)) {
      return;
    }

    // Check user corrections for suppression
    const userCorrection = userCorrections[tagLower];
    if (userCorrection && userCorrection.action === 'blacklist_not_food') {
      return; // Completely suppress from registry
    }

    const crData = foodCrossReferenceMap.get(tag);
    const spikeCount = crData ? crData.adverseMeals.length : 0;
    const distinctDays = crData ? crData.triggerDates.size : 0;
    const soloCalmCount = crData ? crData.soloCalmCount : 0;
    const soloFlareCount = crData ? crData.soloFlareCount : 0;
    const coOccurringCount = crData ? crData.coOccurringFlareCount : 0;

    const totalTimesEaten = eatenData.count;
    const reactionRate = Math.min(100, Math.round((spikeCount / Math.max(1, totalTimesEaten)) * 100));

    // Calculate Relative Risk (RR) and Lift vs Baseline
    const mealsWithoutThisFood = Math.max(1, chronologicalMeals.length - totalTimesEaten);
    const flaresWithoutThisFood = Math.max(0, baselineAdverseMealsCount - spikeCount);
    const rateWithoutFood = Math.max(0.05, flaresWithoutThisFood / mealsWithoutThisFood);
    const foodReactionProb = spikeCount / Math.max(1, totalTimesEaten);
    const relativeRisk = Math.round((foodReactionProb / rateWithoutFood) * 10) / 10;
    const liftVsBaselinePercent = Math.round((foodReactionProb - baselineFlareRate) * 100);
    const baselineSymptomRate = Math.round(baselineFlareRate * 100);

    const avgLag =
      crData && crData.lags.length > 0
        ? Math.round((crData.lags.reduce((a, b) => a + b, 0) / crData.lags.length) * 10) / 10
        : 0;

    const catInfo = getClinicalCategory(tag, speed);
    const lastDateMs = new Date(eatenData.lastDate).getTime();
    const todayMs = new Date().getTime();
    const daysSinceLastEaten = Math.max(0, Math.floor((todayMs - lastDateMs) / (1000 * 60 * 60 * 24)));

    // Calculate elimination clearance if eliminated >= 3 days
    let eliminationClearancePercent: number | undefined = undefined;
    if (daysSinceLastEaten >= 3 && spikeCount >= 1) {
      const preSymptoms = symptoms.filter((s) => new Date(s.date).getTime() <= lastDateMs);
      const postSymptoms = symptoms.filter((s) => new Date(s.date).getTime() > lastDateMs);
      if (preSymptoms.length >= 2 && postSymptoms.length >= 1) {
        const preAvg = preSymptoms.reduce((acc, s) => acc + s.bloating_score + s.soreness_score, 0) / preSymptoms.length;
        const postAvg = postSymptoms.reduce((acc, s) => acc + s.bloating_score + s.soreness_score, 0) / postSymptoms.length;
        if (preAvg > 0 && postAvg < preAvg) {
          eliminationClearancePercent = Math.min(95, Math.round(((preAvg - postAvg) / preAvg) * 100));
        }
      }
    }

    const isDiaryCalibrating = baseline.daysLogged < 14;

    // Check for reintroduction challenge (eaten again after >= 14 day elimination outside calibration)
    let isReintroduced = false;
    let reintroductionResult: 'Tolerated' | 'Triggered Flare' | undefined = undefined;

    const mealsWithThisFood = mealsByFood.get(tag) || [];

    if (!isDiaryCalibrating && mealsWithThisFood.length >= 2) {
      for (let i = 1; i < mealsWithThisFood.length; i++) {
        const gapMs = new Date(mealsWithThisFood[i].timestamp_iso).getTime() - new Date(mealsWithThisFood[i - 1].timestamp_iso).getTime();
        const gapDays = gapMs / (1000 * 60 * 60 * 24);
        if (gapDays >= 14) {
          const reintroMeal = mealsWithThisFood[i];
          const reintroMealTime = new Date(reintroMeal.timestamp_iso).getTime();
          const subsequentSymptoms = symptoms.filter((s) => {
            const lagH = (new Date(s.timestamp_iso).getTime() - reintroMealTime) / (1000 * 60 * 60);
            return lagH >= catInfo.minHours && lagH <= catInfo.maxHours;
          });
          const hasReaction = subsequentSymptoms.some((s) => isFlareSymptom(s, baseline));
          isReintroduced = true;
          if (hasReaction) {
            reintroductionResult = 'Triggered Flare';
          } else {
            reintroductionResult = 'Tolerated';
          }
          break;
        }
      }
    }

    let status: IntoleranceStatus = 'Watchlist';
    let isExoneratedByCrossReference = false;
    let crossReferenceNote: string | undefined = undefined;
    let isUserVerified = false;
    let userCorrectionAction: string | undefined = undefined;

    // Monash dataset and staple match
    const canonical = canonicalizeIngredient(tag);
    const isStaple = isZeroFodmapFood(canonical) || isZeroFodmapFood(tag);
    const matchedFodmap = matchIngredientToFodmap(tag).matchedFood;
    const swapSuggestion = matchedFodmap?.swapSuggestion || undefined;

    const motilityGapCount = crData ? crData.motilityGapFlareCount : 0;

    // -------------------------------------------------------------
    // USER CORRECTION OVERRIDES (GROUND TRUTH TAKES PRECEDENCE)
    // -------------------------------------------------------------
    if (userCorrection) {
      isUserVerified = true;
      userCorrectionAction = userCorrection.action;
      if (userCorrection.action === 'mark_tolerated') {
        status = 'Tolerated (User Verified)';
        isExoneratedByCrossReference = true;
        crossReferenceNote = 'Marked as well tolerated by you. Excluded from culprit warnings.';
      } else if (userCorrection.action === 'confirm_trigger') {
        status = 'Confirmed Culprit (User Verified)';
        crossReferenceNote = 'Confirmed as a known intolerance/trigger by you.';
      }
    }

    // -------------------------------------------------------------
    // STATISTICAL & DIFFERENTIAL CROSS-REFERENCING LOGIC
    // -------------------------------------------------------------
    if (!isUserVerified) {
      if (soloCalmCount >= 1 && soloFlareCount === 0 && coOccurringCount > 0) {
        // Exonerated: Food was eaten in calm meals without flares; past flares were co-occurrences with other aggravators
        status = 'Tolerated (Ruled Out by Cross-Ref)';
        isExoneratedByCrossReference = true;
        crossReferenceNote = `Eaten ${soloCalmCount}x in calm meals without flare. Previous flare was attributed to co-occurring irritants.`;
      } else if (soloCalmCount >= 1 && motilityGapCount > 0 && soloFlareCount <= motilityGapCount) {
        // Exonerated: Flares coincided strictly with missed bowel movement days (motility delay / colonic stagnation)
        status = 'Tolerated (Ruled Out by Cross-Ref)';
        isExoneratedByCrossReference = true;
        crossReferenceNote = `Eaten ${soloCalmCount}x in calm meals. Flare occurred on a missed bowel movement day (motility delay / colonic stagnation) rather than an allergic food response.`;
      } else if (isStaple && soloFlareCount === 0 && spikeCount <= 1) {
        status = 'Safe Baseline';
        isExoneratedByCrossReference = true;
        crossReferenceNote = 'Certified Low-FODMAP staple; well tolerated across meals.';
      } else if (totalTimesEaten < 4) {
        // Statistical Power Gate: N < 4 exposures is insufficient evidence to brand a food as culprit
        status = 'Observing (Gathering Data)';
      } else if (relativeRisk <= 1.15) {
        // Neutral / Tolerated: User flares just as often when NOT eating this food
        status = 'Safe Baseline';
      } else if (isDiaryCalibrating) {
        // During calibration (< 14 days), require minimum repeat distinct flares before tagging Preliminary Suspect
        if (
          !isStaple &&
          distinctDays >= 2 &&
          ((totalTimesEaten >= 4 && spikeCount >= 2 && relativeRisk >= 1.45) ||
          (spikeCount >= 3 && reactionRate >= 40))
        ) {
          status = 'Preliminary Suspect';
        } else if (totalTimesEaten >= 2 && spikeCount === 0) {
          status = 'Safe Baseline';
        } else {
          status = 'Watchlist';
        }
      } else if (isReintroduced) {
        status = reintroductionResult === 'Triggered Flare' ? 'Confirmed Culprit' : 'Tolerated';
      } else if (!isStaple && daysSinceLastEaten >= 3 && spikeCount >= 2 && relativeRisk >= 1.45) {
        status = 'Eliminating';
      } else if (
        !isStaple &&
        totalTimesEaten >= 6 &&
        distinctDays >= 2 &&
        spikeCount >= 3 &&
        relativeRisk >= 1.75 &&
        (soloFlareCount >= 1 || totalTimesEaten >= 8)
      ) {
        status = 'Confirmed Culprit';
      } else if (
        !isStaple &&
        distinctDays >= 2 &&
        totalTimesEaten >= 4 &&
        spikeCount >= 2 &&
        relativeRisk >= 1.45
      ) {
        status = 'Emerging Suspect';
      } else if (totalTimesEaten >= 4 && (spikeCount === 0 || relativeRisk <= 1.2)) {
        status = 'Safe Baseline';
      } else {
        status = 'Watchlist';
      }
    }

    const hasDelayedReaction = crData ? crData.lags.some((l) => l > cfg.colonicMaxHours && l <= cfg.delayedImmuneMaxHours) : false;
    const transitPhase = getTransitPhase(avgLag, speed);

    results.push({
      tag,
      status,
      category: catInfo.category,
      expectedLagLabel: catInfo.lagLabel,
      totalTimesEaten,
      adverseReactionCount: spikeCount,
      reactionRate,
      relativeRisk,
      liftVsBaselinePercent,
      baselineSymptomRate,
      isUserVerified,
      userCorrectionAction,
      distinctTriggerDays: distinctDays,
      averageLagHours: avgLag,
      transitPhase,
      hasDelayedReaction,
      lastEatenDate: eatenData.lastDate,
      daysSinceLastEaten,
      eliminationClearancePercent,
      eliminationDays: daysSinceLastEaten >= 3 ? daysSinceLastEaten : undefined,
      isReintroduced,
      reintroductionResult,
      highestSymptomScores: crData
        ? { bloat: crData.maxBloat, soreness: crData.maxSoreness }
        : undefined,
      isExoneratedByCrossReference,
      crossReferenceNote,
      soloCalmCount,
      coOccurringAggravatorCount: coOccurringCount,
      swapSuggestion,
    });
  });

  const statusOrder: Record<IntoleranceStatus, number> = {
    'Confirmed Culprit (User Verified)': 0,
    'Confirmed Culprit': 1,
    'Persistent Culprit': 2,
    'Eliminating': 3,
    'Emerging Suspect': 4,
    'Preliminary Suspect': 5,
    'Observing (Gathering Data)': 6,
    'Watchlist': 7,
    'Tolerated (Ruled Out by Cross-Ref)': 8,
    'Tolerated (User Verified)': 9,
    'Tolerated': 10,
    'Safe Baseline': 11,
  };

  const CORE_CLINICAL_INTOLERANCES = new Set([
    'gluten', 'milk', 'dairy', 'soy', 'eggs', 'garlic', 'onion', 'coffee', 'alcohol', 'nuts', 'wheat', 'bread', 'pasta'
  ]);

  return results.sort((a, b) => {
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    // Prioritize recognized clinical food intolerances
    const aIsCore = CORE_CLINICAL_INTOLERANCES.has(a.tag) ? 1 : 0;
    const bIsCore = CORE_CLINICAL_INTOLERANCES.has(b.tag) ? 1 : 0;
    if (bIsCore !== aIsCore) return bIsCore - aIsCore;

    // Weight reaction rate by sample size so repeat offenders outrank isolated single-meal exposures
    const scoreA = Math.round(a.reactionRate * (a.totalTimesEaten / (a.totalTimesEaten + 1)));
    const scoreB = Math.round(b.reactionRate * (b.totalTimesEaten / (b.totalTimesEaten + 1)));
    if (scoreB !== scoreA) return scoreB - scoreA;
    if (b.adverseReactionCount !== a.adverseReactionCount) return b.adverseReactionCount - a.adverseReactionCount;
    return b.totalTimesEaten - a.totalTimesEaten;
  });
}

export interface SafeRecoveryMealSuggestion {
  id: string;
  title: string;
  category: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack';
  description: string;
  ingredients: string[];
  safeReason: string;
}

export function generateSafeRecoveryMeals(
  meals: Meal[],
  symptoms: Symptom[],
  speed: TransitSpeed = 'Normal'
): SafeRecoveryMealSuggestion[] {
  const longitudinalStats = computeLongitudinalIntoleranceStats(meals, symptoms, undefined, speed);
  const safeItems = longitudinalStats
    .filter((s) => s.status === 'Safe Baseline' || s.status === 'Tolerated' || s.status === 'Tolerated (Ruled Out by Cross-Ref)')
    .map((s) => s.tag.toLowerCase());

  const hasSafe = (item: string) => safeItems.some((s) => s.includes(item) || item.includes(s));

  const suggestions: SafeRecoveryMealSuggestion[] = [
    {
      id: 'rec-1',
      title: 'Gentle Poached Chicken & Steamed Jasmine Rice',
      category: 'Dinner',
      description: 'Ultra-low residue plate with lean poultry and easily absorbed simple starch to rest the small bowel.',
      ingredients: [
        hasSafe('chicken') ? 'Chicken Breast' : 'Lean Poultry',
        'Jasmine Rice',
        'Steamed Zucchini',
        'Garlic-Infused Olive Oil',
        'Salt',
      ],
      safeReason: '100% Zero-FODMAP proteins & starches. Free from alliums, lactose, and fermentable fibers.',
    },
    {
      id: 'rec-2',
      title: 'Warm Rolled Oats with Blueberries & Maple',
      category: 'Breakfast',
      description: 'Gentle soluble-fiber breakfast that stabilizes motility without colonic gas generation.',
      ingredients: [
        'Rolled Oats (Gluten-Free)',
        'Lactose-Free Milk',
        'Fresh Blueberries',
        'Pure Maple Syrup',
        'Chia Seeds',
      ],
      safeReason: 'Certified low-FODMAP portion (Monash <60g oats, <125g blueberries). Free from dairy sugars.',
    },
    {
      id: 'rec-3',
      title: 'Herb-Baked Salmon with Yukon Potatoes & Carrots',
      category: 'Lunch',
      description: 'Anti-inflammatory omega-3 rich fish paired with gentle root vegetables for zero-distension digestion.',
      ingredients: [
        'Wild Salmon Fillet',
        'Yukon Gold Potatoes',
        'Tender Baby Carrots',
        'Fresh Rosemary & Thyme',
        'Extra Virgin Olive Oil',
      ],
      safeReason: 'Monash lab-tested zero fermentable carbohydrate load; optimal for acute gut recovery days.',
    },
  ];

  return suggestions;
}

export interface SymptomTriggerLikelihood {
  ingredient: string;
  likelihoodPercent: number; // 0 - 100%
  confidence: 'High' | 'Moderate' | 'Potential';
  category: ClinicalFoodCategory;
  lagLabel: string;
  timesEaten: number;
  reactionCount: number;
  avgLagHours: number;
}

/**
 * Retrieves the 0-10 numerical score for a given symptom from a Symptom record.
 */
export function getSymptomScore(symptom: Symptom, targetSymptomId: string): number {
  const target = targetSymptomId.toLowerCase().trim().replace(/[- ]/g, '_');

  // Bowel-movement logs have no rated symptoms (their bloating/pain values are placeholders).
  if (isStoolOnlyEntry(symptom)) return 0;

  if (symptom.symptom_scores && symptom.symptom_scores[target] !== undefined) {
    return symptom.symptom_scores[target];
  }

  if (target === 'bloat' || target === 'bloating') {
    return symptom.bloating_score || 0;
  }
  if (target === 'stomach_pain' || target === 'soreness' || target === 'pain') {
    return symptom.soreness_score || 0;
  }

  if (symptom.symptom_scores) {
    for (const [k, v] of Object.entries(symptom.symptom_scores)) {
      if (k.toLowerCase().replace(/[- ]/g, '_') === target) {
        return v;
      }
    }
  }

  // 0–10 check-ins list every rated symptom in symptom_scores; a mention in the text isn't a rating.
  if (isTenScaleSymptom(symptom)) return 0;

  const tags = (symptom.symptom_tags || '').toLowerCase();
  const notes = (symptom.notes || '').toLowerCase();
  const text = `${tags} ${notes}`;
  const cleanTarget = target.replace(/_/g, ' ');

  if (text.includes(cleanTarget)) {
    return 6; // Assigned moderate severity for legacy tags
  }

  return 0;
}

/**
 * Checks if a specific symptom record matches the requested symptom identifier (score >= 4 or tagged).
 */
export function isSymptomMatch(symptom: Symptom, targetSymptomId: string): boolean {
  const score = getSymptomScore(symptom, targetSymptomId);
  return score >= 4;
}

/**
 * Computes ranked suspected ingredients and their likelihood percentage for a chosen symptom.
 */
export function computeSymptomSpecificTriggers(
  meals: Meal[],
  symptoms: Symptom[],
  symptomId: string,
  timeframeStartMs?: number
): SymptomTriggerLikelihood[] {
  const filteredMeals = timeframeStartMs
    ? meals.filter((m) => new Date(m.timestamp_iso).getTime() >= timeframeStartMs)
    : meals;

  const filteredSymptoms = timeframeStartMs
    ? symptoms.filter((s) => new Date(s.timestamp_iso).getTime() >= timeframeStartMs)
    : symptoms;

  const matchingSymptoms = filteredSymptoms.filter((s) => isSymptomMatch(s, symptomId));

  if (matchingSymptoms.length === 0 || filteredMeals.length === 0) {
    return [];
  }

  // Map each ingredient to its exposure statistics
  const ingredientStats = new Map<
    string,
    {
      totalEaten: number;
      reactionCount: number;
      lags: number[];
    }
  >();

  // Count total times each ingredient was eaten
  filteredMeals.forEach((meal) => {
    const foods = extractFoodsFromMeal(meal);
    foods.forEach((food) => {
      const normalized = food.toLowerCase().trim();
      if (!normalized || normalized.length < 2) return;
      const current = ingredientStats.get(normalized) || { totalEaten: 0, reactionCount: 0, lags: [] };
      current.totalEaten += 1;
      ingredientStats.set(normalized, current);
    });
  });

  // Check which ingredients preceded matching symptom episodes
  matchingSymptoms.forEach((symptom) => {
    const sTime = new Date(symptom.timestamp_iso).getTime();
    if (isNaN(sTime)) return;

    // Look back up to 48 hours for potential triggers
    const precedingMeals = filteredMeals.filter((m) => {
      const mTime = new Date(m.timestamp_iso).getTime();
      if (isNaN(mTime)) return false;
      const lagHours = (sTime - mTime) / (1000 * 60 * 60);
      return lagHours >= 0.5 && lagHours <= 48;
    });

    const seenIngredientsInWindow = new Set<string>();

    precedingMeals.forEach((m) => {
      const mTime = new Date(m.timestamp_iso).getTime();
      const lagHours = (sTime - mTime) / (1000 * 60 * 60);
      const foods = extractFoodsFromMeal(m);

      foods.forEach((food) => {
        const normalized = food.toLowerCase().trim();
        const catInfo = getClinicalCategory(normalized);
        // Strictly evaluate within the food category's biological lag window
        if (lagHours < catInfo.minHours || lagHours > catInfo.maxHours) return;

        if (seenIngredientsInWindow.has(normalized)) return;
        seenIngredientsInWindow.add(normalized);

        const stat = ingredientStats.get(normalized);
        if (stat) {
          stat.reactionCount += 1;
          stat.lags.push(lagHours);
        }
      });
    });
  });

  const results: SymptomTriggerLikelihood[] = [];

  ingredientStats.forEach((stat, rawTag) => {
    if (stat.totalEaten === 0 || stat.reactionCount === 0) return;

    const catInfo = getClinicalCategory(rawTag);
    const avgLag = stat.lags.length > 0
      ? Math.round((stat.lags.reduce((a, b) => a + b, 0) / stat.lags.length) * 10) / 10
      : 0;

    const rawRate = (stat.reactionCount / stat.totalEaten) * 100;
    // Bayesian dampening: small sample sizes (e.g. eaten 1 time, reacted 1 time) are dampened slightly
    const sampleWeight = stat.totalEaten / (stat.totalEaten + 1.2);
    const likelihood = Math.min(96, Math.max(15, Math.round(rawRate * sampleWeight)));

    let confidence: 'High' | 'Moderate' | 'Potential' = 'Potential';
    if (likelihood >= 70 && stat.reactionCount >= 2) {
      confidence = 'High';
    } else if (likelihood >= 45 || stat.reactionCount >= 2) {
      confidence = 'Moderate';
    }

    const displayName = rawTag.charAt(0).toUpperCase() + rawTag.slice(1);

    results.push({
      ingredient: displayName,
      likelihoodPercent: likelihood,
      confidence,
      category: catInfo.category,
      lagLabel: catInfo.lagLabel,
      timesEaten: stat.totalEaten,
      reactionCount: stat.reactionCount,
      avgLagHours: avgLag,
    });
  });

  return results.sort((a, b) => {
    if (b.likelihoodPercent !== a.likelihoodPercent) return b.likelihoodPercent - a.likelihoodPercent;
    return b.reactionCount - a.reactionCount;
  });
}
export interface LineOfBestFitResult {
  scoreStart: number;
  scoreEnd: number;
  slope: number;
  intercept: number;
  avg: number;
  trend: 'improving' | 'elevating' | 'stable';
}

/**
 * Calculates a mathematically rigorous Line of Best Fit (least-squares linear regression)
 * for a set of data points (progress in [0, 1] vs score in [0, 10]).
 */
export function computeLineOfBestFit(dataPoints: { progress: number; val: number }[]): LineOfBestFitResult {
  if (dataPoints.length === 0) {
    return { scoreStart: 0, scoreEnd: 0, slope: 0, intercept: 0, avg: 0, trend: 'stable' };
  }

  if (dataPoints.length === 1) {
    const v = dataPoints[0].val;
    return { scoreStart: v, scoreEnd: v, slope: 0, intercept: v, avg: v, trend: 'stable' };
  }

  const n = dataPoints.length;
  let sumP = 0;
  let sumV = 0;
  let sumPV = 0;
  let sumPP = 0;

  for (let i = 0; i < n; i++) {
    const pt = dataPoints[i];
    sumP += pt.progress;
    sumV += pt.val;
    sumPV += pt.progress * pt.val;
    sumPP += pt.progress * pt.progress;
  }

  const denominator = n * sumPP - sumP * sumP;
  const slope = denominator !== 0 ? (n * sumPV - sumP * sumV) / denominator : 0;
  const intercept = denominator !== 0 ? (sumV - slope * sumP) / n : dataPoints[0].val;

  // Clamp predicted scores to the valid 0-10 symptom scale
  const scoreStart = Math.max(0, Math.min(10, intercept));
  const scoreEnd = Math.max(0, Math.min(10, slope * 1 + intercept));
  const avg = Number((sumV / n).toFixed(1));

  // Positive slope on symptom severity means symptom is worsening (elevating)
  let trend: 'improving' | 'elevating' | 'stable' = 'stable';
  if (slope > 0.35) trend = 'elevating';
  else if (slope < -0.35) trend = 'improving';

  return { scoreStart, scoreEnd, slope, intercept, avg, trend };
}

/**
 * Identifies gaps without stool logs as inferred Constipation / Colonic Stagnation periods.
 * Threshold is dynamically adjusted according to user's transit speed baseline (<24h for normal, <16h for fast).
 */
export function inferConstipationPeriods(
  symptoms: Symptom[],
  meals: Meal[] = [],
  speed: TransitSpeed = 'Normal'
): InferredConstipationPeriod[] {
  const cfg = getTransitWindowConfig(speed);
  // Transit-aware threshold: 16h for Fast, 24h for Normal, 36h for Slow, 48h for Sluggish
  const thresholdHours = Math.max(16, Math.min(48, cfg.colonicMaxHours || 24));

  const stoolSymptoms = symptoms
    .filter((s) => s.stool_type !== null && s.stool_type !== undefined)
    .sort((a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime());

  if (stoolSymptoms.length < 1 && meals.length < 1) return [];

  const periods: InferredConstipationPeriod[] = [];

  // 1. Check sequential gaps between recorded stools
  for (let i = 1; i < stoolSymptoms.length; i++) {
    const prevTime = new Date(stoolSymptoms[i - 1].timestamp_iso).getTime();
    const currTime = new Date(stoolSymptoms[i].timestamp_iso).getTime();
    const gapHours = (currTime - prevTime) / (1000 * 60 * 60);

    if (gapHours >= thresholdHours) {
      const prior = meals.filter((m) => {
        const mTime = new Date(m.timestamp_iso).getTime();
        return mTime >= prevTime && mTime <= currTime;
      });
      periods.push({
        startDate: stoolSymptoms[i - 1].date,
        endDate: stoolSymptoms[i].date,
        unloggedHours: Math.round(gapHours),
        priorMeals: prior,
        gapType: 'Historical Stagnation Gap',
      });
    }
  }

  // 2. Check if current time is >= thresholdHours since most recent stool log
  if (stoolSymptoms.length > 0) {
    const lastStool = stoolSymptoms[stoolSymptoms.length - 1];
    const lastTime = new Date(lastStool.timestamp_iso).getTime();
    const nowTime = Date.now();
    const gapHours = (nowTime - lastTime) / (1000 * 60 * 60);

    if (gapHours >= thresholdHours) {
      const prior = meals.filter((m) => {
        const mTime = new Date(m.timestamp_iso).getTime();
        return mTime >= lastTime;
      });
      periods.push({
        startDate: lastStool.date,
        endDate: new Date().toISOString().slice(0, 10),
        unloggedHours: Math.round(gapHours),
        priorMeals: prior,
        gapType: 'Active Current Gap',
      });
    }
  }

  return periods;
}

/**
 * Computes comprehensive Motility & Bowel Movement Kinetics:
 * - Real-time hours since last bowel movement
 * - Daily evacuation rhythm (BM/day)
 * - Evacuation Days vs Gap Days Bloating impact comparison
 * - Post-evacuation relief measurement
 * - Motility-driven vs food-antigen distension percentage
 */
export function computeMotilityKinetics(
  symptoms: Symptom[],
  meals: Meal[] = [],
  speed: TransitSpeed = 'Normal'
): MotilityKineticsSummary {
  const cfg = getTransitWindowConfig(speed);
  const thresholdHours = Math.max(16, Math.min(48, cfg.colonicMaxHours || 24));
  const constipationPeriods = inferConstipationPeriods(symptoms, meals, speed);

  const stoolSymptoms = symptoms
    .filter((s) => s.stool_type !== null && s.stool_type !== undefined)
    .sort((a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime());

  // 1. Stool Dates & Gap Dates
  const allLoggedDates = new Set<string>();
  const evacuationDates = new Set<string>();
  symptoms.forEach((s) => allLoggedDates.add(s.date));
  meals.forEach((m) => allLoggedDates.add(m.date));

  stoolSymptoms.forEach((s) => evacuationDates.add(s.date));

  const totalLoggedDays = Math.max(1, allLoggedDates.size);
  const evacuationDaysCount = evacuationDates.size;
  const gapDaysCount = Math.max(0, totalLoggedDays - evacuationDaysCount);

  // 2. Real-time hours since last bowel movement
  let hoursSinceLastBowelMovement: number | null = null;
  let lastBowelMovementDateStr: string | null = null;
  let lastBowelMovementBristol: number | null = null;

  if (stoolSymptoms.length > 0) {
    const last = stoolSymptoms[stoolSymptoms.length - 1];
    const lastTime = new Date(last.timestamp_iso).getTime();
    hoursSinceLastBowelMovement = Math.max(0, Math.round((Date.now() - lastTime) / (1000 * 60 * 60)));
    lastBowelMovementDateStr = last.date;
    lastBowelMovementBristol = last.stool_type ?? null;
  }

  // 3. Current Status
  let currentStatus: MotilityKineticsSummary['currentStatus'] = 'No Data Logged';
  if (stoolSymptoms.length > 0) {
    if (hoursSinceLastBowelMovement !== null && hoursSinceLastBowelMovement <= thresholdHours) {
      currentStatus = 'Regular Daily Rhythm';
    } else if (hoursSinceLastBowelMovement !== null && hoursSinceLastBowelMovement <= thresholdHours * 1.5) {
      currentStatus = 'Delayed Evacuation';
    } else {
      currentStatus = 'Active Colonic Stagnation';
    }
  }

  // 4. Stool Frequency / Rhythm per day
  const totalRecordedStools = stoolSymptoms.length;
  const evacuationRhythmPerDay = Number((totalRecordedStools / totalLoggedDays).toFixed(1));

  // 5. Bloating comparison: Evacuation Days vs Gap Days
  let evacBloatSum = 0;
  let evacBloatCount = 0;
  let gapBloatSum = 0;
  let gapBloatCount = 0;

  for (const s of symptoms) {
    const bloat = s.bloating_score || s.symptom_scores?.bloating || 0;
    if (evacuationDates.has(s.date)) {
      evacBloatSum += bloat;
      evacBloatCount++;
    } else {
      gapBloatSum += bloat;
      gapBloatCount++;
    }
  }

  const avgBloatOnEvacuationDays = evacBloatCount > 0 ? Number((evacBloatSum / evacBloatCount).toFixed(1)) : 1.5;
  const avgBloatOnGapDays = gapBloatCount > 0 ? Number((gapBloatSum / gapBloatCount).toFixed(1)) : Number((avgBloatOnEvacuationDays + 1.8).toFixed(1));
  const bloatDeltaOnGapDays = Number((avgBloatOnGapDays - avgBloatOnEvacuationDays).toFixed(1));

  // 6. Post-Evacuation Relief: Measure bloat drop within 2-6h post-BM
  const reliefDeltas: number[] = [];
  for (const stool of stoolSymptoms) {
    const stoolTime = new Date(stool.timestamp_iso).getTime();
    const preSymptoms = symptoms.filter((s) => {
      const t = new Date(s.timestamp_iso).getTime();
      const lag = (stoolTime - t) / (1000 * 60 * 60);
      return lag > 0 && lag <= 6;
    });
    const postSymptoms = symptoms.filter((s) => {
      const t = new Date(s.timestamp_iso).getTime();
      const lag = (t - stoolTime) / (1000 * 60 * 60);
      return lag > 0 && lag <= 6;
    });

    if (preSymptoms.length > 0 && postSymptoms.length > 0) {
      const preAvg = preSymptoms.reduce((sum, s) => sum + s.bloating_score, 0) / preSymptoms.length;
      const postAvg = postSymptoms.reduce((sum, s) => sum + s.bloating_score, 0) / postSymptoms.length;
      const drop = postAvg - preAvg;
      if (drop < 0) {
        reliefDeltas.push(drop);
      }
    }
  }

  const postEvacuationReliefAverage = reliefDeltas.length > 0
    ? Number((reliefDeltas.reduce((a, b) => a + b, 0) / reliefDeltas.length).toFixed(1))
    : -1.8;

  // 7. Motility-driven bloat percentage: Bloating spikes (>=4) that occurred on non-evacuation/gap days
  const elevatedBloatSymptoms = symptoms.filter(
    (s) => !isStoolOnlyEntry(s) && (s.bloating_score >= 4 || (s.symptom_scores?.bloating || 0) >= 4)
  );
  let motilitySpikesCount = 0;
  for (const s of elevatedBloatSymptoms) {
    if (!evacuationDates.has(s.date)) {
      motilitySpikesCount++;
    }
  }
  const motilityDrivenBloatPercent = elevatedBloatSymptoms.length > 0
    ? Math.round((motilitySpikesCount / elevatedBloatSymptoms.length) * 100)
    : 0;

  // 8. Bristol Distribution
  let hardCount = 0;
  let optimalCount = 0;
  let looseCount = 0;
  for (const s of stoolSymptoms) {
    const type = s.stool_type;
    if (type !== null && type !== undefined) {
      if (type <= 2) hardCount++;
      else if (type <= 4) optimalCount++;
      else looseCount++;
    }
  }
  const totalStoolsForDist = Math.max(1, hardCount + optimalCount + looseCount);
  const bristolDistribution = {
    hardConstipationPercent: Math.round((hardCount / totalStoolsForDist) * 100),
    optimalPercent: Math.round((optimalCount / totalStoolsForDist) * 100),
    looseUrgencyPercent: Math.round((looseCount / totalStoolsForDist) * 100),
  };

  // 9. Clinical recommendation
  let clinicalRecommendation = 'Maintain daily hydration and active movement to support smooth gastrointestinal peristalsis.';
  if (gapDaysCount > evacuationDaysCount || currentStatus === 'Active Colonic Stagnation') {
    clinicalRecommendation = 'Noticeable colonic evacuation delay observed. Stagnation is a major contributor to distension bloat; prioritize morning warm fluids, gentle post-meal walking, and soluble fiber.';
  } else if (bloatDeltaOnGapDays >= 1.5) {
    clinicalRecommendation = `Days without bowel movements increase your average bloating by +${bloatDeltaOnGapDays} pts. Regular daily motility significantly protects against distension.`;
  } else if (bristolDistribution.optimalPercent >= 70) {
    clinicalRecommendation = 'Excellent stool consistency and regular bowel rhythm. Colonic motility is well-balanced.';
  }

  return {
    hoursSinceLastBowelMovement,
    lastBowelMovementDateStr,
    lastBowelMovementBristol,
    currentStatus,
    evacuationRhythmPerDay,
    totalRecordedStools,
    totalLoggedDays,
    evacuationDaysCount,
    gapDaysCount,
    avgBloatOnEvacuationDays,
    avgBloatOnGapDays,
    bloatDeltaOnGapDays,
    postEvacuationReliefAverage,
    motilityDrivenBloatPercent,
    bristolDistribution,
    constipationPeriods,
    clinicalRecommendation,
  };
}

/**
 * Computes longitudinal empirical correlations for each of the 6 FODMAP subtypes
 * (Fructans, GOS, Lactose, Excess Fructose, Sorbitol, Mannitol) based on chemical
 * composition data and delayed colonic symptoms (4h-24h lag).
 */
export function computeFodmapSubtypeCorrelations(
  meals: Meal[],
  symptoms: Symptom[],
  speed: TransitSpeed = 'Normal'
): FodmapSubtypeCorrelation[] {
  const cfg = getTransitWindowConfig(speed);
  const subtypeList: Array<{ subtype: FodmapSubtype; key: 'fructans' | 'gos' | 'lactose' | 'fructose' | 'sorbitol' | 'mannitol'; emoji: string }> = [
    { subtype: 'Fructans', key: 'fructans', emoji: '🌾' },
    { subtype: 'GOS', key: 'gos', emoji: '🫘' },
    { subtype: 'Lactose', key: 'lactose', emoji: '🥛' },
    { subtype: 'Excess Fructose', key: 'fructose', emoji: '🍏' },
    { subtype: 'Sorbitol', key: 'sorbitol', emoji: '🥑' },
    { subtype: 'Mannitol', key: 'mannitol', emoji: '🍄' },
  ];

  const statsMap = new Map<FodmapSubtype, {
    totalExposures: number;
    flaresCount: number;
    sources: Set<string>;
    severitySum: number;
    severityCount: number;
  }>();

  for (const item of subtypeList) {
    statsMap.set(item.subtype, {
      totalExposures: 0,
      flaresCount: 0,
      sources: new Set(),
      severitySum: 0,
      severityCount: 0,
    });
  }

  // Pre-calculate which meals contain significant loads of each subtype
  const mealSubtypes = meals.map((meal) => {
    const activeSubtypes = new Set<FodmapSubtype>();
    const mult = meal.portionMultiplier || 1.0;
    const allIngredients = meal.dishes && meal.dishes.length > 0
      ? meal.dishes.flatMap((d) => (d.ingredients || []).map((i) => ({
          ...i,
          gramsEstimate: (i.gramsEstimate || 100) * (d.portionMultiplier || 1.0),
        })))
      : (meal.ingredients && meal.ingredients.length > 0
          ? meal.ingredients.map((i) => ({
              ...i,
              gramsEstimate: (i.gramsEstimate || 100) * mult,
            }))
          : (meal.ingredients_tags || meal.description || ''));

    const sideScale = calculateDetailedFodmapSideScale(allIngredients);

    for (const it of sideScale.items) {
      if (it.percentage >= 40 || it.level >= 2) {
        const matchingSub = subtypeList.find((s) => s.key === it.id);
        if (matchingSub) {
          activeSubtypes.add(matchingSub.subtype);
        }
      }
    }

    const tags = (meal.ingredients_tags || '').split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    for (const tag of tags) {
      const canonical = canonicalizeIngredient(tag);
      if (isZeroFodmapFood(canonical) || isZeroFodmapFood(tag)) continue;
      const ev = matchIngredientToFodmap(tag);
      const stackable = getFoodStackableSubtypes(tag, ev.matchedFood, ev.primaryCulprit);
      for (const st of stackable) {
        activeSubtypes.add(st);
        statsMap.get(st)?.sources.add(tag);
      }
    }

    return {
      meal,
      timeMs: new Date(meal.timestamp_iso).getTime(),
      activeSubtypes: Array.from(activeSubtypes),
    };
  });

  // Track total exposures
  for (const m of mealSubtypes) {
    for (const st of m.activeSubtypes) {
      const entry = statsMap.get(st);
      if (entry) entry.totalExposures += 1;
    }
  }

  // Correlate with lower GI symptom flares in colonic window (4h - cfg.colonicMaxHours)
  const lowerGiSymptoms = symptoms.filter((s) => {
    const bloat = s.bloating_score || s.symptom_scores?.bloating || 0;
    const sore = s.soreness_score || s.symptom_scores?.stomach_pain || 0;
    const stool = s.stool_type;
    const isLoose = stool !== null && stool !== undefined && stool >= 5;
    return bloat >= 3 || sore >= 3 || isLoose;
  });

  for (const sym of lowerGiSymptoms) {
    const symTimeMs = new Date(sym.timestamp_iso).getTime();
    const bloat = sym.bloating_score || sym.symptom_scores?.bloating || 0;
    const sore = sym.soreness_score || sym.symptom_scores?.stomach_pain || 0;
    const severity = Math.max(bloat, sore, 3);

    const preceding = mealSubtypes.filter((m) => {
      const lagHours = (symTimeMs - m.timeMs) / (1000 * 60 * 60);
      return lagHours >= 3.5 && lagHours <= (cfg.colonicMaxHours || 24);
    });

    const evaluatedSubtypesForThisSymptom = new Set<FodmapSubtype>();
    for (const p of preceding) {
      for (const st of p.activeSubtypes) {
        if (!evaluatedSubtypesForThisSymptom.has(st)) {
          evaluatedSubtypesForThisSymptom.add(st);
          const entry = statsMap.get(st);
          if (entry) {
            entry.flaresCount += 1;
            entry.severitySum += severity;
            entry.severityCount += 1;
          }
        }
      }
    }
  }

  // Finalize reports for all 6 subtypes
  return subtypeList.map(({ subtype, emoji }) => {
    const st = statsMap.get(subtype)!;
    const total = st.totalExposures;
    const flares = Math.min(total, st.flaresCount);
    const flareRate = total > 0 ? Math.round((flares / total) * 100) : 0;
    const avgSeverity = st.severityCount > 0 ? Math.round((st.severitySum / st.severityCount) * 10) / 10 : 0;

    let status: 'Suspected Trigger' | 'Tolerated / Ruled Out' | 'Insufficient Data' = 'Insufficient Data';
    let clinicalNote = '';

    if (total >= 2 && flareRate >= 40) {
      status = 'Suspected Trigger';
      clinicalNote = `${flareRate}% of exposures preceded lower GI distension/symptoms (N=${flares}/${total}). Strong correlation with colonic fermentation.`;
    } else if (total >= 3 && flareRate <= 25) {
      status = 'Tolerated / Ruled Out';
      clinicalNote = `Eaten ${total} times with ${flares === 0 ? 'zero' : 'minimal'} adverse reactions (${flareRate}% flare rate). Can likely be ruled out as an active trigger.`;
    } else if (total === 0) {
      status = 'Insufficient Data';
      clinicalNote = 'No significant dietary exposures logged yet.';
    } else {
      status = 'Insufficient Data';
      clinicalNote = `Eaten ${total} times (${flares} associated discomfort events). Continue logging to reach clinical confidence.`;
    }

    return {
      subtype,
      emoji,
      totalMealsCount: total,
      followedBySymptomCount: flares,
      flareRatePercent: flareRate,
      status,
      primarySources: Array.from(st.sources).slice(0, 5),
      avgSeverity,
      clinicalNote,
    };
  });
}



